import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Container, Compass, Check, X } from "lucide-react";
import { envDetect, envDisableTraefik, envInstall } from "../../redux/actionCreator";
import "./envPanel.scss";

/**
 * "Docker Desktop"-style setup & health panel for one server.
 *
 * Detects what's installed (Docker, kubectl, k3s) and shows status. When a
 * piece is missing, offers a one-click install that runs the upstream script
 * over SSH (k3s → get.k3s.io, Docker → get.docker.com) using the server's
 * stored sudo password, streaming the combined output into a console pane.
 */
export default function EnvPanel({ show, onHide, project, server }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [installing, setInstalling] = useState(null); // "k3s" | "docker" | null
  const [repairing, setRepairing] = useState(false);
  const [output, setOutput] = useState(null); // { ok, what, text }

  const projectId = project?._id;
  const serverId = server?._id;

  const detect = useCallback(async () => {
    if (!projectId || !serverId) return;
    setLoading(true);
    setErr(null);
    const res = await dispatch(envDetect(projectId, serverId));
    setLoading(false);
    if (res?.status) setData(res.data);
    else setErr(res?.message || "Detection failed");
  }, [dispatch, projectId, serverId]);

  useEffect(() => {
    if (show) {
      setData(null);
      setOutput(null);
      detect();
    }
  }, [show, detect]);

  const install = async (what) => {
    const label = what === "k3s" ? "k3s (Kubernetes)" : "Docker";
    if (!window.confirm(
      `Install ${label} on ${server?.label || server?.host}?\n\nThis runs the official install script over SSH with sudo. It can take a minute or two.`
    )) return;
    setInstalling(what);
    setOutput({ ok: null, what, text: `Installing ${label}… this can take a minute.` });
    const res = await dispatch(envInstall(projectId, serverId, what));
    setInstalling(null);
    setOutput({
      ok: !!res?.status,
      what,
      text: res?.data?.output || res?.message || (res?.status ? "Done." : "Install failed"),
    });
    // Re-probe so the status badges update.
    detect();
  };

  const disableTraefik = async () => {
    if (!window.confirm(
      "Disable k3s' Traefik & servicelb and restart k3s + nginx?\n\nThis frees ports 80/443 so nginx serves your subdomains again. Brief blip while k3s restarts."
    )) return;
    setRepairing(true);
    setOutput({ ok: null, what: "disable Traefik", text: "Reconfiguring k3s… restarting k3s + waiting for the API, then nginx. This takes 1–2 minutes — leave this open." });
    const res = await dispatch(envDisableTraefik(projectId, serverId));
    setRepairing(false);
    setOutput({ ok: !!res?.status, what: "disable Traefik", text: res?.data?.output || res?.message || (res?.status ? "Done." : "Failed") });
    detect();
  };

  const k = data?.kubernetes;
  const d = data?.docker;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="env-panel">
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <h4 className="m-0">Environment</h4>
          <span className="text-muted">{server?.label}</span>
          <code className="small">{server?.username}@{server?.host}</code>
        </div>
      </Modal.Header>
      <Modal.Body>
        {loading && !data ? (
          <div className="text-center py-4"><Spinner animation="border" /> <span className="ms-2">Probing server…</span></div>
        ) : err ? (
          <div className="alert alert-danger">
            {err}
            <div className="mt-2"><Button size="sm" variant="light" onClick={detect}>Retry</Button></div>
          </div>
        ) : data ? (
          <>
            <div className="env-host-meta">
              <span><strong>OS:</strong> {data.os}</span>
              <span><strong>Arch:</strong> {data.arch}</span>
              {!data.sudo_password_stored && (
                <Badge bg="warning" text="dark" title="Install needs root. Store a sudo password on the server, or use a root SSH user.">
                  no sudo password stored
                </Badge>
              )}
            </div>

            {/* Docker card */}
            <div className="env-card">
              <div className="env-card-head">
                <div>
                  <span className="env-icon"><Container size={20} /></span>
                  <strong>Docker</strong>
                  {d?.installed ? (
                    <Badge bg={d.running ? "success" : "secondary"} className="ms-2">
                      {d.running ? "running" : "installed (daemon stopped)"}
                    </Badge>
                  ) : (
                    <Badge bg="secondary" className="ms-2">not installed</Badge>
                  )}
                </div>
                {!d?.installed && (
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={!!installing || !data.sudo_password_stored}
                    onClick={() => install("docker")}
                  >
                    {installing === "docker" ? <Spinner size="sm" animation="border" /> : "Install Docker"}
                  </Button>
                )}
              </div>
              {d?.installed && (
                <div className="env-card-detail">
                  <div>{d.version}</div>
                  {d.compose && <div className="text-muted small">{d.compose}</div>}
                </div>
              )}
            </div>

            {/* Kubernetes card */}
            <div className="env-card">
              <div className="env-card-head">
                <div>
                  <span className="env-icon"><Compass size={20} /></span>
                  <strong>Kubernetes</strong>
                  {k?.k3s_installed ? (
                    <Badge bg={k.cluster_reachable ? "success" : "secondary"} className="ms-2">
                      {k.cluster_reachable ? `cluster up · ${k.node_count} node${k.node_count === 1 ? "" : "s"}` : `k3s ${k.k3s_active}`}
                    </Badge>
                  ) : k?.kubectl_installed ? (
                    <Badge bg="info" className="ms-2">kubectl only (no local cluster)</Badge>
                  ) : (
                    <Badge bg="secondary" className="ms-2">not installed</Badge>
                  )}
                </div>
                <div className="d-flex gap-2">
                  {k?.k3s_installed && (
                    <Button
                      size="sm"
                      variant="outline-warning"
                      disabled={repairing || !!installing || !data.sudo_password_stored}
                      onClick={disableTraefik}
                      title="Disable k3s' Traefik + servicelb so nginx keeps ports 80/443"
                    >
                      {repairing ? <Spinner size="sm" animation="border" /> : "Free 80/443 (disable Traefik)"}
                    </Button>
                  )}
                  {!k?.k3s_installed && (
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={!!installing || !data.sudo_password_stored}
                      onClick={() => install("k3s")}
                    >
                      {installing === "k3s" ? <Spinner size="sm" animation="border" /> : "Install k3s"}
                    </Button>
                  )}
                </div>
              </div>
              {(k?.k3s_installed || k?.kubectl_installed) && (
                <div className="env-card-detail">
                  {k.k3s_version && <div>{k.k3s_version}</div>}
                  {k.kubectl_version && <div className="text-muted small">kubectl {k.kubectl_version}</div>}
                  {k?.k3s_installed && (
                    <div className="text-muted small mt-1">
                      k3s bundles Traefik on 80/443. If your subdomains show a “TRAEFIK DEFAULT CERT” warning, use <strong>Free 80/443</strong> so nginx serves them.
                    </div>
                  )}
                </div>
              )}
            </div>

            {output && (
              <div className={`env-console ${output.ok === true ? "ok" : output.ok === false ? "bad" : ""}`}>
                <div className="env-console-head">
                  <strong>
                    <span className="d-inline-flex align-items-center gap-1">
                      {output.ok === null ? "Installing…" : output.ok ? <><Check size={12} /> Done</> : <><X size={12} /> Failed</>}
                    </span> — {output.what}
                  </strong>
                  {output.ok !== null && (
                    <button type="button" className="btn-close-x" onClick={() => setOutput(null)}>×</button>
                  )}
                </div>
                <pre>{output.text}</pre>
              </div>
            )}
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={detect} disabled={loading || !!installing}>
          {loading ? <Spinner size="sm" animation="border" /> : "↻ Re-scan"}
        </Button>
        <Button variant="light" onClick={onHide} disabled={!!installing}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
